import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IApiRequest, ISeriesResponse } from "@miplatform/types";

export const useSeries = () => {
  const [series, setSeries] = useState<ISeriesResponse[]>([]);
  const [specLevelOptions, setSpecLevelOptions] = useState<string[]>([]);

  const stateManager = useComponentState();

  const getSeries = ({
    filters,
    sort = "name",
    params,
    signal,
    top,
    skip,
    search,
  }: IApiRequest = {}) => {
    return stateManager.run(async () => {
      const response = await homeService.series.getAll({
        filters: {
          ...(search && { name: { contains: search } }),
          ...filters,
        },
        sort,
        params,
        signal,
        top,
        skip,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setSeries(response.data);
      }
      return response;
    });
  };

  const createSeries = (data: Omit<ISeriesResponse, "id">) => {
    return stateManager.run(async () => {
      return homeService.series.create({
        data,
        toastMessageConfig: {
          success: {
            text: `Successfully created Series: ${data.name}.`,
          },
        },
      });
    });
  };

  const updateSeries = (data: Partial<ISeriesResponse>) => {
    return stateManager.run(async () => {
      return homeService.series.update({
        data,
        toastMessageConfig: {
          success: {
            text: `Successfully updated Series: ${data.name}.`,
          },
        },
      });
    });
  };

  const deleteSeries = (data: Partial<ISeriesResponse>) => {
    return stateManager.run(async () => {
      return homeService.series.delete({
        id: data.id,
        modifiedOn: data.modifiedOn,
        toastMessageConfig: {
          success: {
            text: `Series ${data.name} was deleted successfully.`,
          },
        },
      });
    });
  };

  const getSpecLevelSuggestions = ({
    searchText,
    accountId,
  }: {
    searchText?: string;
    accountId?: string;
  } = {}) => {
    return stateManager.run(async () => {
      const response = await homeService.series.specLevel.getSuggestions({
        searchText,
        accountId,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setSpecLevelOptions(response.data);
      }
      return response;
    });
  };

  return {
    isLoading: stateManager.isBusy(),
    series,
    specLevelOptions,
    setSeries,
    getSeries,
    createSeries,
    updateSeries,
    deleteSeries,
    getSpecLevelSuggestions,
  };
};
